import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import baseUrl from "../../../../api/baseUrl";
import {
  AbsentClash,
  CallNote,
  RescheduleSlot,
  bookingTypeLabel,
  cancelSummary,
  formatIsoDate,
  formatTime12,
  futureSlots,
  minutesBetween,
  movableClashes,
  smsProblem,
  toBackendTime,
} from "./absentClashUtils";

type DentistOption = { _id: string; name: string };

/** The absence being saved; its own time is not offered as a new slot. */
export type PendingAbsence = {
  date: string;
  doctorId: string;
  startTime: string;
  endTime: string;
};

function errorMessage(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { message?: string; error?: string } } };
  return ax?.response?.data?.error || ax?.response?.data?.message || fallback;
}

function minutesOf(t: string): number {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t ?? ""));
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
}

function overlaps(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return minutesOf(aStart) < minutesOf(bEnd) && minutesOf(aEnd) > minutesOf(bStart);
}

function RescheduleForm({
  clash,
  dentists,
  absence,
  onDone,
  onClose,
}: {
  clash: AbsentClash;
  dentists: DentistOption[];
  absence: PendingAbsence;
  onDone: (note: CallNote | null) => void;
  onClose: () => void;
}) {
  const [date, setDate] = useState(absence.date);
  const [doctorId, setDoctorId] = useState(
    () => dentists.find((d) => d._id !== absence.doctorId)?._id ?? absence.doctorId
  );
  const [slots, setSlots] = useState<RescheduleSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState<RescheduleSlot | null>(null);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!date || !doctorId) return;
    let cancelled = false;
    setLoading(true);
    setPicked(null);
    baseUrl
      .get("/client/setting/dentistAbsentDate/rescheduleSlots", {
        params: { appointmentId: clash._id, doctorId, date },
      })
      .then((res) => {
        if (cancelled) return;
        const raw: RescheduleSlot[] = Array.isArray(res.data?.data?.slots) ? res.data.data.slots : [];
        const sameAbsence = doctorId === absence.doctorId && date === absence.date;
        setSlots(
          futureSlots(raw, date, new Date()).filter(
            (s) =>
              !sameAbsence || !overlaps(s.startTime, s.endTime, absence.startTime, absence.endTime)
          )
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setSlots([]);
        toast.error(errorMessage(err, "Could not load free times"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clash._id, date, doctorId, absence, reloadKey]);

  const dentistName = dentists.find((d) => d._id === doctorId)?.name ?? "";

  const confirm = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      const res = await baseUrl.patch(
        "/appointment/reschedule",
        {
          doctorId,
          date,
          startTime: toBackendTime(picked.startTime),
          endTime: toBackendTime(picked.endTime),
          interval: String(minutesBetween(picked.startTime, picked.endTime)),
          sendSms: true,
        },
        { headers: { appointmentid: clash._id, status: "appointment" } }
      );
      const when = `${formatIsoDate(date)}, ${formatTime12(picked.startTime)}`;
      const problem = smsProblem(res.data?.sms);
      if (problem) {
        toast.warn(`${clash.patientName} moved to ${when}. SMS not sent: ${problem}`);
      } else {
        toast.success(`${clash.patientName} moved to ${when}. SMS sent.`);
      }
      onDone(
        problem
          ? {
              appointmentId: clash._id,
              patientName: clash.patientName,
              mobileNo: clash.mobileNo,
              action: "rescheduled",
              detail: `New time ${when} with ${dentistName}`,
              reason: problem,
            }
          : null
      );
    } catch (err) {
      toast.error(errorMessage(err, "Could not reschedule"));
      setReloadKey((k) => k + 1);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="space-y-1 block">
          <div className="text-xs font-semibold text-slate-600">New date</div>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-1.5 rounded-md border border-slate-200 bg-white text-sm text-slate-800 outline-none focus:border-primary"
          />
        </label>
        <label className="space-y-1 block">
          <div className="text-xs font-semibold text-slate-600">Dentist</div>
          <select
            value={doctorId}
            onChange={(e) => setDoctorId(e.target.value)}
            className="w-full px-3 py-1.5 rounded-md border border-slate-200 bg-white text-sm text-slate-800 outline-none focus:border-primary"
          >
            {dentists.map((d) => (
              <option key={d._id} value={d._id}>
                {d.name}
                {d._id === absence.doctorId ? " (absent dentist)" : ""}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div>
        <div className="text-xs font-semibold text-slate-600 mb-1">Free times</div>
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : slots.length === 0 ? (
          <div className="text-sm text-slate-500">
            No free time for this dentist on this day. Try another day or dentist.
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
            {slots.map((s) => {
              const active = picked?.startTime === s.startTime;
              return (
                <button
                  key={s.startTime}
                  type="button"
                  onClick={() => setPicked(s)}
                  className={
                    active
                      ? "px-2 py-1 rounded text-xs font-semibold bg-slate-900 text-white"
                      : "px-2 py-1 rounded text-xs font-medium bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
                  }
                >
                  {formatTime12(s.startTime)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 rounded-md text-xs font-semibold bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
        >
          Back
        </button>
        <button
          type="button"
          onClick={confirm}
          disabled={!picked || saving}
          className="px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {saving ? "Moving…" : "Move and text patient"}
        </button>
      </div>
    </div>
  );
}

export default function AbsentClashPanel({
  clashes,
  loading,
  dentists,
  absence,
  callNotes,
  onChanged,
  onCallNotes,
}: {
  clashes: AbsentClash[];
  loading: boolean;
  dentists: DentistOption[];
  absence: PendingAbsence;
  callNotes: CallNote[];
  onChanged: () => void;
  onCallNotes: (notes: CallNote[]) => void;
}) {
  const [rescheduleId, setRescheduleId] = useState<string | null>(null);
  const [cancelIds, setCancelIds] = useState<string[] | null>(null);
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const movable = useMemo(() => movableClashes(clashes), [clashes]);
  const inClinicCount = clashes.length - movable.length;

  useEffect(() => {
    // A row that left the list (moved, cancelled elsewhere) closes its form.
    if (rescheduleId && !clashes.some((c) => c._id === rescheduleId)) setRescheduleId(null);
    if (cancelIds) {
      const still = cancelIds.filter((id) => clashes.some((c) => c._id === id));
      if (still.length !== cancelIds.length) setCancelIds(still.length ? still : null);
    }
  }, [clashes, rescheduleId, cancelIds]);

  const openCancel = (ids: string[]) => {
    setRescheduleId(null);
    setCancelIds(ids);
  };

  const confirmCancel = async () => {
    if (!cancelIds?.length || !reason.trim()) return;
    setCancelling(true);
    try {
      const res = await baseUrl.post("/appointment/cancelForAbsence", {
        appointmentIds: cancelIds,
        reason: reason.trim(),
      });
      const results: Array<{
        _id: string;
        cancelled?: boolean;
        alreadyCancelled?: boolean;
        error?: string;
        sms?: { sent?: boolean; message?: string };
      }> = res.data?.data?.results ?? [];
      const byId = new Map(clashes.map((c) => [c._id, c]));
      const notes: CallNote[] = [];
      for (const r of results) {
        const c = byId.get(r._id);
        if (!c || !r.cancelled || r.alreadyCancelled) continue;
        const problem = smsProblem(r.sms);
        if (problem) {
          notes.push({
            appointmentId: c._id,
            patientName: c.patientName,
            mobileNo: c.mobileNo,
            action: "cancelled",
            detail: `Was ${formatTime12(c.startTime)}`,
            reason: problem,
          });
        }
      }
      for (const r of results) {
        if (!r.cancelled && r.error) {
          toast.error(`${byId.get(r._id)?.patientName ?? "Appointment"}: ${r.error}`);
        }
      }
      const s = cancelSummary(results);
      const tail = s.notTexted ? ` ${s.notTexted} not texted, call them.` : " Patients texted.";
      if (s.cancelled) toast.success(`Cancelled ${s.cancelled}.${tail}`);
      if (notes.length) onCallNotes(notes);
      setCancelIds(null);
      setReason("");
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, "Could not cancel"));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-3">
      {clashes.length > 0 ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="font-semibold">
            {clashes.length} appointment{clashes.length === 1 ? " is" : "s are"} booked in this time.
          </span>{" "}
          Reschedule or cancel each one before saving.
          {inClinicCount > 0
            ? ` ${inClinicCount} ${inClinicCount === 1 ? "patient is" : "patients are"} already in the clinic; finish those on the appointments screen.`
            : ""}
        </div>
      ) : (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          <span className="font-semibold">All clear.</span> Nobody is booked in this time. Click Save.
        </div>
      )}

      {clashes.length > 0 ? (
        <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 max-h-[45vh] overflow-y-auto">
          {clashes.map((c) => {
            const typeLabel = bookingTypeLabel(c.bookingType);
            return (
              <li key={c._id} className="px-3 py-2">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-semibold text-slate-800 tabular-nums">
                        {formatTime12(c.startTime)}
                      </span>
                      <span className="text-sm font-medium text-slate-800 truncate">
                        {c.patientName}
                      </span>
                      {typeLabel ? (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600">
                          {typeLabel}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-500">
                      {[c.mobileNo, c.regNo, c.purposes.join(", ")].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {c.kind === "in_clinic" ? (
                    <div className="text-xs font-medium text-slate-500 sm:text-right">
                      In the clinic now
                    </div>
                  ) : (
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setCancelIds(null);
                          setRescheduleId((id) => (id === c._id ? null : c._id));
                        }}
                        className="px-2 py-1 rounded text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200"
                      >
                        Reschedule
                      </button>
                      <button
                        type="button"
                        onClick={() => openCancel([c._id])}
                        className="px-2 py-1 rounded text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                {rescheduleId === c._id ? (
                  <RescheduleForm
                    clash={c}
                    dentists={dentists}
                    absence={absence}
                    onClose={() => setRescheduleId(null)}
                    onDone={(note) => {
                      setRescheduleId(null);
                      if (note) onCallNotes([note]);
                      onChanged();
                    }}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {loading ? <div className="text-xs text-slate-500">Checking…</div> : null}

      {cancelIds?.length ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-2">
          <div className="text-sm font-semibold text-red-800">
            Cancel {cancelIds.length} appointment{cancelIds.length === 1 ? "" : "s"} and text{" "}
            {cancelIds.length === 1 ? "the patient" : "each patient"}
          </div>
          <label className="space-y-1 block">
            <div className="text-xs font-semibold text-slate-600">Reason (kept on the appointment)</div>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Dentist unwell"
              className="w-full px-3 py-1.5 rounded-md border border-slate-200 bg-white text-sm text-slate-800 outline-none focus:border-primary"
            />
          </label>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setCancelIds(null)}
              className="px-3 py-1.5 rounded-md text-xs font-semibold bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              Back
            </button>
            <button
              type="button"
              onClick={confirmCancel}
              disabled={!reason.trim() || cancelling}
              className="px-3 py-1.5 rounded-md text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {cancelling ? "Cancelling…" : "Cancel and text"}
            </button>
          </div>
        </div>
      ) : movable.length > 1 ? (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => openCancel(movable.map((c) => c._id))}
            className="px-3 py-1.5 rounded-md text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100"
          >
            Cancel all ({movable.length})
          </button>
        </div>
      ) : null}

      {callNotes.length > 0 ? (
        <div className="rounded-md border border-slate-300 bg-white p-3">
          <div className="text-sm font-semibold text-slate-800">Not texted: please call</div>
          <ul className="mt-1 space-y-1">
            {callNotes.map((n) => (
              <li key={`${n.appointmentId}-${n.action}`} className="text-xs text-slate-700">
                <span className="font-semibold">{n.patientName}</span>
                {n.mobileNo ? ` · ${n.mobileNo}` : ""} · {n.action}. {n.detail}.{" "}
                <span className="text-slate-500">({n.reason})</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
